/**
 * Banking signup step definitions, lookup helpers, and progress calculations.
 *
 * Split into three files:
 * - `stepDefinitions.types.ts`   – TypeScript interfaces & enums
 * - `stepDefinitions.helpers.ts` – PATCH body builders, dedup, wrapper props
 * - `stepDefinitions.ts` (this)  – MAIN_STEPS data + registry/lookup functions
 */
import { AvailableCountries } from 'types/users.types';
import {
  PreAccountStepId,
  type SwanOnboardingStepIdType,
} from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboardingStepIds';
import type {
  BankingSignupAccountDetails,
  BankingSignupBornDetailsFormValues,
  BankingSignupResidencyFormValues,
  BirthInfo,
} from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboarding.types';
import {
  PostAccountStepId,
  type SwanActivationStepIdType,
} from '../ActivationFlow/constants/activationStepIds';
import { StepComponentName } from '../ActivationFlow/constants/componentNames';
import {
  normalizeAddressCountryAlpha3,
  userCountryToOnboardingAlpha3,
} from './onboardingCountryCode';

// Analytics events
import {
  WEBAPP_BANKING_ONBOARDING_VISITEDMEETACCOUNTABLEBAKINGSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDPEPPOLSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDISSUECARDSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDACCIBANONINVOICESSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDTAXESDESCRIPTIONSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEOURPROMISESSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPERSONALDETAILSFORMSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPIDENTITYCONFIRMATIONSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDIDENTITYSUMMARYSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDBORNDETAILSSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDRESIDENCYDETAILSSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDPERSONALDETAILSSUMMARYSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDBUSINESSADDRESSSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDBUSINESSACTIVITYSCREEN,
  // Post-account activation events
  WEBAPP_BANKING_ACTIVATION_VISITEDINITACTIVATIONSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDHOWISINTEGRATEDSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDTAXESDESCRIPTIONSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDACCIBANONINVOICESSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDISSUECARDSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDCARDDESIGNSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDYOURCARDREADYSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDCARDTOWALLETSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDTOPUPSCREEN,
  WEBAPP_BANKING_ACTIVATION_VISITEDREADYTOGOSCREEN,
} from 'utils/amplitude/events/bank.amplitude';

// Icons
import NewBankIcon from 'assets/svg/icons/newBankIcon';
import PlugOnIcon from 'assets/svg/icons/plugOnIcon';
import UploadOutline from 'assets/svg/icons/UploadOutline';
import MoneyReceive2 from 'assets/svg/icons/MoneyReceive2';
import ConfettiPartyIcon from 'assets/svg/icons/confettiPartyIcon';
import { UserIcon, IdCardIcon, DocumentUpload } from 'utils/icons/figmaIcons';
import type { CountryAlpha3Code } from 'types/countries';

// Re-export types so consumers keep importing from one place
export * from './stepDefinitions.types';

// Re-export helpers for direct use in renderProps below and by consumers
export {
  getWrapperProps,
  bankingSignupOnboardingBase,
  bankingSignupActivityFields,
  persistOnboardingThenContinue,
} from './stepDefinitions.helpers';

// Local imports for use inside MAIN_STEPS renderProps
import {
  getWrapperProps,
  bankingSignupOnboardingBase,
  bankingSignupActivityFields,
  persistOnboardingThenContinue,
} from './stepDefinitions.helpers';

import { StepMode } from './stepDefinitions.types';
import type {
  MainStepDefinition,
  SubstepDefinition,
  StepRenderContext,
} from './stepDefinitions.types';

// ===========================
// MAIN STEP DEFINITIONS
// ===========================

/**
 * Complete step hierarchy for Accountable Banking flow.
 * Organized by main steps with substeps. Read top-to-bottom.
 *
 * Pre-account steps: signup intro → personal details → proof of registration → verify identity → first transfer
 * Post-account steps: embed banking (activation flow)
 */
export const MAIN_STEPS: MainStepDefinition[] = [
  // ==========================================
  // PRE-ACCOUNT: STEP 1 - Sign up with Accountable
  // ==========================================
  {
    id: 'signup_intro',
    label: 'accountable_banking.activation.init_activation.step_1',
    listIcon: NewBankIcon,
    substeps: [
      {
        id: PreAccountStepId.InitOnboarding,
        mode: StepMode.PreAccount,
        onboardingStep: PreAccountStepId.InitOnboarding,
        componentName: StepComponentName.MeetAccountableBanking,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEDMEETACCOUNTABLEBAKINGSCREEN,
        renderProps: (ctx) => ({
          ...getWrapperProps(ctx),
          onContinue: ctx.onContinue,
          isUpdatingStep: ctx.isUpdatingStep,
        }),
      },
      {
        id: 'peppol_banking',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.PeppolBanking,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDPEPPOLSCREEN,
        renderProps: (ctx) => ({
          ...getWrapperProps(ctx),
          onContinue: ctx.onContinue,
        }),
        countrySpecific: [AvailableCountries.belgium],
      },
      {
        id: PreAccountStepId.ExpensesDescription,
        mode: StepMode.PreAccount,
        onboardingStep: PreAccountStepId.ExpensesDescription,
        componentName: StepComponentName.IssueCardStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDISSUECARDSCREEN,
        titleKey: 'accountable_banking.activation.issue_card.title',
        subtitleKey: 'accountable_banking.activation.issue_card.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          isPreview: true,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PreAccountStepId.AbIbanOnInvoices,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.AccIbanOnInvoicesOnboardingStep,
        onboardingStep: PreAccountStepId.AbIbanOnInvoices,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEDACCIBANONINVOICESSCREEN,
        titleKey: 'accountable_banking.activation.acc_iban_on_invoices.title',
        subtitleKey:
          'accountable_banking.activation.acc_iban_on_invoices.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          isPreAccount: true,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PreAccountStepId.TaxesDescription,
        mode: StepMode.PreAccount,
        onboardingStep: PreAccountStepId.TaxesDescription,
        componentName: StepComponentName.TaxesDescription,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDTAXESDESCRIPTIONSCREEN,
        titleKey: 'accountable_banking.activation.taxes_description.title',
        subtitleKey:
          'accountable_banking.activation.taxes_description.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
      // `notifications` is its own screen on mobile; web has no substep for it—this disclosures
      // screen sits at `advantages` and reports `notifications` so backend progress matches mobile.
      {
        id: PreAccountStepId.Advantages,
        mode: StepMode.PreAccount,
        onboardingStep: PreAccountStepId.Notifications,
        componentName: StepComponentName.FullTransparency,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEOURPROMISESSCREEN,
        renderProps: (ctx) => ({
          onConfirm: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 2 - Provide personal details
  // ==========================================
  {
    id: 'personal_details',
    label: 'accountable_banking.activation.init_activation.step_2',
    listIcon: UserIcon,
    substeps: [
      {
        id: PreAccountStepId.SummaryDetails,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.SummaryStep,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEDPERSONALDETAILSSUMMARYSCREEN,
        modalIcon: NewBankIcon,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          summaryCurrentSubstepId: PreAccountStepId.SummaryDetails,
          summaryStepsScope: StepMode.PreAccount,
          summaryUserCountry: ctx.user?.country as AvailableCountries,
          showProgressBar: false,
          ...getWrapperProps(ctx),
          title: ctx.t(
            'accountable_banking.onboarding.pre_account_summary.title',
            {
              count: getRemainingStepsCount(
                PreAccountStepId.SummaryDetails,
                StepMode.PreAccount,
                ctx.user?.country as AvailableCountries,
              ),
            },
          ),
        }),
      },
      {
        id: PreAccountStepId.PersonalInfoForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.PersonalInfoStep,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEPERSONALDETAILSFORMSCREEN,
        titleKey: 'personal_details',
        renderProps: (ctx) => ({
          currentAccountDetailsValues: ctx.accountDetails,
          onConfirm: (values: BankingSignupAccountDetails) => {
            ctx.setAccountDetails(values);
            return persistOnboardingThenContinue(
              ctx,
              bankingSignupOnboardingBase(ctx, values),
            );
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
      // ── DE flow: Address → Activity (API call on activity) ──
      {
        id: PreAccountStepId.BusinessAddressForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.BusinessAddressStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDBUSINESSADDRESSSCREEN,
        countrySpecific: [AvailableCountries.germany],
        titleKey: 'personal_details',
        renderProps: (ctx) => ({
          currentAccountDetailsValues: ctx.accountDetails,
          onConfirm: (values: BankingSignupAccountDetails) => {
            ctx.setAccountDetails(values);
            return persistOnboardingThenContinue(ctx, {
              ...bankingSignupOnboardingBase(ctx, values),
              businessAddress: {
                city: values?.address?.city ?? '',
                street: values?.address?.street ?? '',
                zip: values?.address?.zip ?? '',
                state: values?.address?.state,
              },
            });
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PreAccountStepId.BusinessActivityForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.BusinessActivityStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDBUSINESSACTIVITYSCREEN,
        countrySpecific: [AvailableCountries.germany],
        titleKey: 'personal_details',
        renderProps: (ctx) => ({
          currentAccountDetailsValues: ctx.accountDetails,
          onConfirm: (values: BankingSignupAccountDetails) => {
            ctx.setAccountDetails(values);

            return persistOnboardingThenContinue(ctx, {
              ...bankingSignupOnboardingBase(ctx, values),
              furthestStepCompleted: PreAccountStepId.PersonalDetails,
              taxID: values?.steuernummer,
              businessAddress: {
                city: values?.address?.city ?? '',
                street: values?.address?.street ?? '',
                zip: values?.address?.zip ?? '',
                state: values?.address?.state,
              },
              ...bankingSignupActivityFields(ctx, values),
            });
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },

      // ── BE flow: Activity → Address (API call on address) ──
      {
        id: PreAccountStepId.BEBusinessActivityForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.BusinessActivityStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDBUSINESSACTIVITYSCREEN,
        countrySpecific: [AvailableCountries.belgium],
        titleKey: 'personal_details',
        renderProps: (ctx) => ({
          currentAccountDetailsValues: ctx.accountDetails,
          onConfirm: (values: BankingSignupAccountDetails) => {
            const merged: BankingSignupAccountDetails = {
              ...values,
              _addressSearchCompleted: true,
            };
            ctx.setAccountDetails(merged);
            return persistOnboardingThenContinue(ctx, {
              ...bankingSignupOnboardingBase(ctx, merged),
              taxID: values?.VATNumber,
              ...bankingSignupActivityFields(ctx, values),
            });
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PreAccountStepId.BEBusinessAddressForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.BusinessAddressStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDBUSINESSADDRESSSCREEN,
        countrySpecific: [AvailableCountries.belgium],
        titleKey: 'personal_details',
        renderProps: (ctx) => ({
          currentAccountDetailsValues: ctx.accountDetails,
          onConfirm: (values: BankingSignupAccountDetails) => {
            ctx.setAccountDetails(values);

            return persistOnboardingThenContinue(ctx, {
              ...bankingSignupOnboardingBase(ctx, values),
              furthestStepCompleted: PreAccountStepId.PersonalDetails,
              taxID: values?.VATNumber,
              businessAddress: {
                city: values?.address?.city ?? '',
                street: values?.address?.street ?? '',
                zip: values?.address?.zip ?? '',
              },
              ...bankingSignupActivityFields(ctx, values),
            });
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 3 - Upload proof of registration
  // ==========================================
  {
    id: 'proof_of_registration',
    label: 'accountable_banking.activation.init_activation.step_3',
    listIcon: UploadOutline,
    countrySpecific: [AvailableCountries.germany],
    substeps: [
      {
        id: PreAccountStepId.SummaryRegistration,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.SummaryStep,
        modalIcon: NewBankIcon,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          summaryCurrentSubstepId: PreAccountStepId.SummaryRegistration,
          summaryStepsScope: StepMode.PreAccount,
          summaryUserCountry: ctx.user?.country,
          showProgressBar: false,
          ...getWrapperProps(ctx),
          title: ctx.t(
            'accountable_banking.onboarding.pre_account_summary.title',
            {
              count: getRemainingStepsCount(
                PreAccountStepId.SummaryRegistration,
                StepMode.PreAccount,
                ctx.user?.country,
              ),
            },
          ),
        }),
      },
      {
        id: PreAccountStepId.ProofOfRegistrationUploader,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.FinanzamtDocUploader,
        modalIcon: DocumentUpload,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN,
        titleKey: 'accountable_banking.onboarding.finanzamt_doc.title.de',
        renderProps: (ctx) => ({
          // todo: add try/catch — rejection currently bubbles to the caller unhandled
          onConfirm: async () => {
            await ctx.onboardingAPI.mutateAsync({
              _id: ctx.onboardingId,
              country: ctx.user?.country,
              furthestStepCompleted: PreAccountStepId.UploadDocument,
              filepaths: ctx.proofOfRegistrationFiles?.map((file) => file.url),
            });
            ctx.onContinue();
          },
          defaultFiles: ctx.proofOfRegistrationFiles,
          files: ctx.proofOfRegistrationFiles,
          setFiles: ctx.setProofOfRegistrationFiles,
          onboardingId: ctx.onboardingId,
          onUploaded: (file: {
            url: string;
            payload: string;
            name?: string;
          }) => {
            ctx.setProofOfRegistrationFiles(
              (files: { url: string; payload: string; name?: string }[]) => [
                ...files,
                file,
              ],
            );
          },
          // todo: should be true but doesnt work correct since we handle all-in-one file (no separate upload step) - to be fixed in future when we split upload and confirmation
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 4 - Verify your identity
  // ==========================================
  {
    id: 'verify_identity',
    label: 'accountable_banking.activation.init_activation.step_4',
    listIcon: IdCardIcon,
    substeps: [
      {
        id: PreAccountStepId.SummaryIdentification,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.SummaryStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDIDENTITYSUMMARYSCREEN,
        modalIcon: NewBankIcon,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          summaryCurrentSubstepId: PreAccountStepId.SummaryIdentification,
          summaryStepsScope: StepMode.PreAccount,
          summaryUserCountry: ctx.user?.country,
          showProgressBar: false,
          ...getWrapperProps(ctx),
          showBackButton: ctx.user?.country !== AvailableCountries.germany,
          title: ctx.t(
            'accountable_banking.onboarding.pre_account_summary.title',
            {
              count: getRemainingStepsCount(
                PreAccountStepId.SummaryIdentification,
                StepMode.PreAccount,
                ctx.user?.country,
              ),
            },
          ),
        }),
      },
      {
        id: PreAccountStepId.BornDetailsForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.BornDetailsStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDBORNDETAILSSCREEN,
        titleKey: 'accountable_banking.onboarding.form.born_details.title',
        subtitleKey:
          'accountable_banking.onboarding.form.born_details.subtitle',
        renderProps: (ctx) => ({
          defaultValues: ctx.birthDetails
            ? {
                birthCity: ctx.birthDetails.city,
                birthPostalCode: ctx.birthDetails.postalCode,
                birthCountry: ctx.birthDetails.country,
                nationality: ctx.birthDetails.nationality,
              }
            : undefined,
          // todo: add try/catch — rejection currently bubbles to the caller unhandled
          onConfirm: async (values: BankingSignupBornDetailsFormValues) => {
            const birth = {
              city: values.birthCity,
              postalCode: values.birthPostalCode,
              country: values.birthCountry,
              nationality: values.nationality,
            };
            ctx.setBirthDetails(birth);

            await ctx.onboardingAPI.mutateAsync({
              _id: ctx.onboardingId,
              country: ctx.user?.country,
              furthestStepCompleted: PreAccountStepId.BornDetails,
              birth,
            });
            ctx.onContinue();
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PreAccountStepId.ResidencyDetailsForm,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.ResidencyDetailsStep,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDRESIDENCYDETAILSSCREEN,
        titleKey: 'residency_details',
        renderProps: (ctx) => ({
          defaultValues: (() => {
            const suggestedCountryUpper =
              ctx.residencyDetails?.country != null
                ? normalizeAddressCountryAlpha3(ctx.residencyDetails.country)
                : userCountryToOnboardingAlpha3(ctx.user?.country);
            const r = ctx.residencyDetails;
            return {
              residencyCountry: suggestedCountryUpper,
              residencyStreet: r?.street ?? '',
              residencyCity: r?.city ?? '',
              residencyZip: r?.zip ?? '',
            };
          })(),
          // todo: add try/catch — rejection currently bubbles to the caller unhandled
          onConfirm: async (values: BankingSignupResidencyFormValues) => {
            const addressCountry = normalizeAddressCountryAlpha3(
              values.residencyCountry as CountryAlpha3Code,
            );
            const residency = {
              street: values.residencyStreet,
              city: values.residencyCity,
              zip: values.residencyZip,
              country:
                addressCountry ??
                userCountryToOnboardingAlpha3(ctx.user?.country),
            };
            ctx.setResidencyDetails(residency);

            await ctx.onboardingAPI.mutateAsync({
              _id: ctx.onboardingId,
              country: ctx.accountDetails?.country ?? ctx.user?.country,
              furthestStepCompleted: PreAccountStepId.ResidencyDetails,
              personalDetails: {
                firstName: ctx.accountDetails?.firstName ?? ctx.user?.firstName,
                lastName: ctx.accountDetails?.lastName ?? ctx.user?.lastName,
                email: ctx.accountDetails?.email ?? ctx.user?.email,
                address: {
                  street: residency.street,
                  city: residency.city,
                  zip: residency.zip,
                  country: addressCountry,
                },
              },
            });
            ctx.onContinue();
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PreAccountStepId.ConfirmIdentity,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.ConfirmIdentity,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEPIDENTITYCONFIRMATIONSCREEN,
        renderProps: (ctx) => ({
          // todo: add try/catch — rejection currently bubbles to the caller unhandled
          onConfirm: async () => {
            const data: { redirectUri: string } =
              await ctx.createBankConnectionAsync({ provider: 'swan' });
            window.location.href = data.redirectUri;
          },
          showProgressBar: true,
          ...getWrapperProps(ctx),
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 5 - Send the first transfer (Germany only)
  // ==========================================
  {
    id: PreAccountStepId.FirstTransfer,
    label: 'accountable_banking.activation.init_activation.step_5',
    listIcon: MoneyReceive2,
    countrySpecific: [AvailableCountries.germany],
    substeps: [
      {
        id: PreAccountStepId.FirstTransfer,
        mode: StepMode.PreAccount,
        componentName: StepComponentName.ReadyToGoStep,
        titleKey: 'accountable_banking.activation.first_transfer.title',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
        }),
      },
    ],
  },

  // ==========================================
  // POST-ACCOUNT: STEP 1 - Embed banking in your Accountable
  // ==========================================
  {
    id: 'embed_banking',
    label: 'accountable_banking.activation.init_activation.step_6',
    listIcon: PlugOnIcon,
    substeps: [
      {
        id: PostAccountStepId.InitActivation,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.InitActivation,
        componentName: StepComponentName.SummaryStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDINITACTIVATIONSCREEN,
        titleKey: 'accountable_banking.activation.init_activation.title',
        modalIcon: NewBankIcon,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.HowIsIntegrated,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.HowIsIntegrated,
        componentName: StepComponentName.HowIsIntegratedStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDHOWISINTEGRATEDSCREEN,
        titleKey: 'accountable_banking.activation.how_is_integrated.title',
        subtitleKey:
          'accountable_banking.activation.how_is_integrated.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.TaxesDescription,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.TaxesDescription,
        componentName: StepComponentName.TaxesDescriptionStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDTAXESDESCRIPTIONSCREEN,
        titleKey: 'accountable_banking.activation.taxes_description.title',
        subtitleKey:
          'accountable_banking.activation.taxes_description.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.AccIbanOnInvoices,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.AccIbanOnInvoices,
        componentName: StepComponentName.AccIbanOnInvoicesActivationStep,
        analyticsEvent:
          WEBAPP_BANKING_ACTIVATION_VISITEDACCIBANONINVOICESSCREEN,
        isBookmark: true,
        titleKey: 'accountable_banking.activation.acc_iban_on_invoices.title',
        subtitleKey:
          'accountable_banking.activation.acc_iban_on_invoices.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.IssueCard,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.IssueCard,
        componentName: StepComponentName.IssueCardStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDISSUECARDSCREEN,
        isBookmark: true,
        titleKey: 'accountable_banking.activation.issue_card.title',
        subtitleKey: 'accountable_banking.activation.issue_card.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onSkipToCardReady: ctx.onSkip
            ? () => ctx.onSkip?.(PostAccountStepId.YourCardReady)
            : undefined,
          onSkip: ctx.onSkip
            ? () => ctx.onSkip?.(PostAccountStepId.TopUp)
            : undefined,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.CardDesign,
        mode: StepMode.PostAccount,
        componentName: StepComponentName.CardDesignStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDCARDDESIGNSCREEN,
        titleKey: 'accountable_banking.activation.issue_card.customize_title',
        subtitleKey:
          'accountable_banking.activation.issue_card.customize_subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.YourCardReady,
        mode: StepMode.PostAccount,
        componentName: StepComponentName.YourCardReadyStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDYOURCARDREADYSCREEN,
        titleKey: 'accountable_banking.activation.card_to_wallet.ready_title',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.CardToWallet,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.CardToWallet,
        componentName: StepComponentName.CardToWalletStep,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDCARDTOWALLETSCREEN,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.TopUp,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.TopUp,
        componentName: StepComponentName.TopUpStep,
        isBookmark: true,
        titleKey: 'accountable_banking.activation.top_up.title',
        subtitleKey: 'accountable_banking.activation.top_up.subtitle',
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDTOPUPSCREEN,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.ReadyToGo,
        mode: StepMode.PostAccount,
        activationStep: PostAccountStepId.ReadyToGo,
        componentName: StepComponentName.ReadyToGoStep,
        titleKey: 'accountable_banking.activation.ready_to_go.title',
        modalIcon: ConfettiPartyIcon,
        analyticsEvent: WEBAPP_BANKING_ACTIVATION_VISITEDREADYTOGOSCREEN,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
    ],
  },
];

// ===========================
// REGISTRY & LOOKUP FUNCTIONS
// ===========================

/**
 * Returns main steps that have at least one substep matching the given scope
 * and applicable to the user's country.
 */
export const getScopedMainSteps = (
  scope: StepMode,
  userCountry?: AvailableCountries,
): MainStepDefinition[] => {
  const isCountryMatch = (countries?: AvailableCountries[]) =>
    !countries || !userCountry || countries.includes(userCountry);

  return MAIN_STEPS.filter(
    (s) =>
      isCountryMatch(s.countrySpecific) &&
      s.substeps.some(
        (sub) => sub.mode === scope && isCountryMatch(sub.countrySpecific),
      ),
  );
};

/** Get main step that contains a given substep. */
export const getMainStepForSubstep = (
  substepId: string,
): MainStepDefinition | undefined => {
  return MAIN_STEPS.find((mainStep) =>
    mainStep.substeps.some((substep) => substep.id === substepId),
  );
};

/**
 * Count how many scoped main steps remain from (and including) the one
 * that contains the given substep.
 * Used for dynamic titles like "{count} steps to open your Banking".
 */
export const getRemainingStepsCount = (
  substepId: string,
  scope: StepMode,
  userCountry?: AvailableCountries,
): number => {
  const scoped = getScopedMainSteps(scope, userCountry);
  const current = getMainStepForSubstep(substepId);
  const idx = current ? scoped.findIndex((s) => s.id === current.id) : -1;
  return idx >= 0 ? scoped.length - idx : scoped.length;
};

/**
 * Flatten main steps into a single array of substeps.
 * Propagates the parent's countrySpecific to substeps that don't define their own.
 */
export const getAllSubsteps = (): SubstepDefinition[] => {
  return MAIN_STEPS.flatMap((mainStep) =>
    mainStep.substeps.map((sub) =>
      !sub.countrySpecific && mainStep.countrySpecific
        ? { ...sub, countrySpecific: mainStep.countrySpecific }
        : sub,
    ),
  );
};

/** Get all post-account backend steps (for progress calculation). */
export const getPostAccountBackendSteps = (): SwanActivationStepIdType[] => {
  return getAllSubsteps()
    .filter(
      (
        substep,
      ): substep is SubstepDefinition & {
        activationStep: SwanActivationStepIdType;
      } => substep.mode === StepMode.PostAccount && !!substep.activationStep,
    )
    .map((substep) => substep.activationStep);
};

/** Find substep by ID. */
export const findSubstepById = (id: string): SubstepDefinition | undefined => {
  return getAllSubsteps().find((substep) => substep.id === id);
};

/**
 * Central mapping: backend `furthestStepCompleted` (last completed step)
 * -> first actionable web substep to resume at.
 *
 * Notes:
 * - Some backend steps are mobile-only (`notifications`), so web resumes at the
 *   corresponding next web screen.
 * - Some mapped substeps are country-specific (`peppol_banking`, `summary_registration`);
 *   resume logic walks forward to the next visible step when needed.
 */
export const PREACCOUNT_STEP_TO_SUBSTEP_ID: Partial<
  Record<SwanOnboardingStepIdType, string>
> = {
  [PreAccountStepId.InitOnboarding]: PreAccountStepId.PeppolBanking,
  [PreAccountStepId.ExpensesDescription]: PreAccountStepId.AbIbanOnInvoices,
  [PreAccountStepId.AbIbanOnInvoices]: PreAccountStepId.TaxesDescription,
  [PreAccountStepId.TaxesDescription]: PreAccountStepId.Advantages,
  // Resume from disclosures at the first data-entry step, not the summary gate.
  [PreAccountStepId.Advantages]: PreAccountStepId.PersonalInfoForm,
  [PreAccountStepId.Notifications]: PreAccountStepId.PersonalInfoForm,
  [PreAccountStepId.PersonalDetails]: PreAccountStepId.SummaryRegistration,
  [PreAccountStepId.UploadDocument]: PreAccountStepId.SummaryIdentification,
  [PreAccountStepId.BornDetails]: PreAccountStepId.ResidencyDetailsForm,
  [PreAccountStepId.ResidencyDetails]: PreAccountStepId.ConfirmIdentity,
  [PreAccountStepId.KycStarted]: PreAccountStepId.ConfirmIdentity,
  [PreAccountStepId.KycCompleted]: PreAccountStepId.FirstTransfer,
};

/** Find substep by sign-up onboarding backend step (post-account activation). */
export const findSubstepByActivationStep = (
  backendStep: SwanActivationStepIdType,
): SubstepDefinition | undefined => {
  return getAllSubsteps().find(
    (substep) => substep.activationStep === backendStep,
  );
};

/**
 * Find substep by sign-up onboarding backend step (pre-account onboarding).
 * Checks PREACCOUNT_STEP_TO_SUBSTEP_ID mapping first, then falls back to onboardingStep field scan.
 */
export const findSubstepByOnboardingStep = (
  onboardingStep: SwanOnboardingStepIdType,
): SubstepDefinition | undefined => {
  const substepId = PREACCOUNT_STEP_TO_SUBSTEP_ID[onboardingStep];
  if (substepId) {
    const byMapping = findSubstepById(substepId);
    if (byMapping) return byMapping;
  }

  return getAllSubsteps().find(
    (substep) => substep.onboardingStep === onboardingStep,
  );
};

// ===========================
// PROGRESS CALCULATIONS
// ===========================

export const ACTIVATION_PROGRESS_START_PERCENTAGE = 75;

export const calculateActivationProgress = (
  furthestStep: SwanActivationStepIdType | null | undefined,
): number => {
  if (!furthestStep) {
    return ACTIVATION_PROGRESS_START_PERCENTAGE;
  }

  const postAccountSteps = getPostAccountBackendSteps();
  const currentIndex = postAccountSteps.indexOf(furthestStep);

  if (currentIndex === -1) {
    return ACTIVATION_PROGRESS_START_PERCENTAGE;
  }

  const remainingPercentage = 100 - ACTIVATION_PROGRESS_START_PERCENTAGE;
  const stepProgress =
    ((currentIndex + 1) / postAccountSteps.length) * remainingPercentage;

  return Math.round(ACTIVATION_PROGRESS_START_PERCENTAGE + stepProgress);
};

/**
 * Frontend-visible onboarding steps in order (excludes backend-only kycStarted/kycCompleted).
 * Used to calculate onboarding progress percentage.
 */
export const ONBOARDING_STEPS_ORDER: SwanOnboardingStepIdType[] = [
  PreAccountStepId.InitOnboarding,
  PreAccountStepId.ExpensesDescription,
  PreAccountStepId.AbIbanOnInvoices,
  PreAccountStepId.TaxesDescription,
  PreAccountStepId.Advantages,
  PreAccountStepId.Notifications,
  PreAccountStepId.PersonalDetails,
  PreAccountStepId.UploadDocument,
  PreAccountStepId.BornDetails,
  PreAccountStepId.ResidencyDetails,
  PreAccountStepId.KycStarted,
  PreAccountStepId.KycCompleted,
];

/**
 * Calculate onboarding progress percentage (0 → ACTIVATION_PROGRESS_START_PERCENTAGE).
 * Dovetails into calculateActivationProgress which starts at 75%.
 */
export const calculateOnboardingProgress = (
  furthestStep: SwanOnboardingStepIdType | null | undefined,
): number => {
  if (!furthestStep) return 0;

  const idx = ONBOARDING_STEPS_ORDER.indexOf(furthestStep);
  if (idx === -1) return 0;

  return Math.round(
    ((idx + 1) / ONBOARDING_STEPS_ORDER.length) *
      ACTIVATION_PROGRESS_START_PERCENTAGE,
  );
};
